import React, { useEffect, useState, useContext } from "react";
import { useSocket } from "../../../context/SocketProvider";
import { ChatState } from "../../../context/ChatProvider";
import { NotificationContext } from "../../../context/NotificationContext";
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { addToRequests, addToChat } from "../../../api/epks";
import { toast } from "react-toastify";

export default function RequestAccessModal({ close, open, user, epkId, filmmakerId, setRefresh }) {
  const socket = useSocket();
  const [requestMsg, setRequestMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setNotification } = ChatState();
  const { incrementMessage, setUserInfo } = useContext(NotificationContext);
  const { t } = useTranslation();

  useEffect(() => {
    // Uses the shared, persistent socket (see SocketProvider) rather than a
    // throwaway one created per mount, so the listener is explicitly cleaned
    // up here instead of relying on socket.disconnect() to drop it.
    if (!socket) return;
    const handleMessageReceived = (newMessageRecieved) => {
      setNotification((prev) =>
        prev.includes(newMessageRecieved) ? prev : [newMessageRecieved, ...prev]
      );
    };
    socket.on("message recieved", handleMessageReceived);
    return () => socket.off("message recieved", handleMessageReceived);
  }, [socket, setNotification]);

  const handleSubmit = async () => {
    if (!requestMsg.trim() || submitting) return;
    setSubmitting(true);
    try {
      const requestRes = await addToRequests(requestMsg, epkId);
      if (!requestRes) throw new Error("Request failed");

      const chatRes = await addToChat(requestMsg, user, filmmakerId);
      if (chatRes.status === 200) {
        incrementMessage();
        setUserInfo(filmmakerId);
        socket?.emit("new message", chatRes.data);
        toast.success(t("Access request sent successfully!"));
        close("request");
        setRefresh(true);
      } else {
        throw new Error("Chat failed");
      }
    } catch {
      toast.error(t("Failed to send access request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-[#190033]/90 tw-backdrop-blur-md tw-p-4">
      <div className="tw-relative tw-w-full tw-max-w-md tw-bg-[#280D41] tw-border tw-border-[#5A3F49]/30 tw-rounded-2xl tw-p-6 tw-shadow-2xl">
        
        <button 
          onClick={() => close("request")} 
          className="tw-absolute tw-top-4 tw-right-4 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-black/20 hover:tw-bg-[#FF43A7] tw-text-white tw-rounded-full tw-transition-colors tw-border-none tw-cursor-pointer"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <h2 className="tw-text-white tw-text-xl tw-font-bold tw-mb-6 tw-text-center">
          {t('Please type your message to the Filmmaker EPK Owner')}
        </h2>

        <div className="tw-mb-6">
          <textarea
            value={requestMsg}
            onChange={(e) => setRequestMsg(e.target.value)}
            maxLength={500}
            placeholder={t("eg. Hello Filmmaker, I’m interested to see your film EPK and possibly purchase the rights. Let’s connect and talk!")}
            className="tw-w-full tw-h-48 tw-bg-[#190033] tw-border tw-border-[#5A3F49]/40 tw-rounded-xl tw-p-4 tw-text-white tw-text-sm tw-outline-none focus:tw-border-[#FF43A7] tw-resize-none placeholder:tw-text-[#AA8894]/60"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={!requestMsg.trim() || submitting}
          className="tw-w-full tw-bg-[#FF43A7] hover:tw-bg-[#ff5cac] tw-text-[#570033] tw-font-bold tw-py-3 tw-rounded-xl tw-shadow-[0_0_15px_rgba(255,67,167,0.4)] tw-transition-all tw-border-none tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          {submitting ? t("Sending...") : t("Submit")}
        </button>
      </div>
    </div>
  );
}